import React, { Component } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Button,
  DatePicker,
  Select,
  Table,
  Icon,
  Spin,
  Empty,
  Modal,
} from "antd";
import NavBar from "../nav/NavBar/NavBar";
import Sidebar from "../../app/common/util/Sidebar";
import { getOrderCountReport } from "./api";
import {
  SUMMARY_CARD_CONFIG,
  DEFAULT_LNAM_GROUPS,
  STATUS_OPTIONS,
  CUSTOMER_CATEGORY_OPTIONS,
  ORDER_TYPE_OPTIONS,
  DLAB_OPTIONS,
} from "./reportsConfig";
import {
  getDefaultDateRange,
  validateReportDateRange,
  buildReportParams,
  formatChangeLabel,
  getSummaryMetricValue,
  getSummaryMetricChange,
  getSummaryChangeClass,
  reportErrorToast,
  MAX_REPORT_RANGE_DAYS,
  isReportSuccess,
  getLockedReportCustomerId,
} from "./reportUtils";
import {
  getCustomerList,
  getDispatchList,
} from "../orderPunching/formUtils";
import { reportError } from "../../app/common/util/sentryConfig";

const { RangePicker } = DatePicker;
const { Option } = Select;

class OrderCountReportPage extends Component {
  state = {
    dateRange: getDefaultDateRange(),
    status: "",
    customerId: undefined,
    customerLocked: false,
    branchId: undefined,
    productType: undefined,
    orderType: "",
    dlabCustomer: "",
    customerCategory: "",
    primaryBranchId: undefined,
    customerList: [],
    branchList: [],
    loading: false,
    reportData: null,
    lnamGroupOptions: DEFAULT_LNAM_GROUPS,
    filtersOpen: false,
  };

  componentDidMount() {
    this.loadFilterOptions().then(() => this.fetchReport());
  }

  loadFilterOptions = async () => {
    const lockedCustomerId = getLockedReportCustomerId();
    const customerLocked = lockedCustomerId != null;

    const [customerList, branchList] = await Promise.all([
      getCustomerList(),
      Promise.resolve(getDispatchList()),
    ]);

    let nextCustomerList = customerList || [];
    if (customerLocked) {
      const lockedOnly = nextCustomerList.filter(
        (item) => String(item.key) === String(lockedCustomerId)
      );
      if (lockedOnly.length) {
        nextCustomerList = lockedOnly;
      }
    }

    return new Promise((resolve) => {
      this.setState(
        {
          customerId: customerLocked ? lockedCustomerId : undefined,
          customerLocked,
          customerList: nextCustomerList,
          branchList: branchList || [],
        },
        resolve
      );
    });
  };

  getFilters = () => {
    const {
      dateRange,
      status,
      customerId,
      branchId,
      productType,
      orderType,
      dlabCustomer,
      customerCategory,
      primaryBranchId,
    } = this.state;
    return {
      startDate: dateRange[0],
      endDate: dateRange[1],
      status,
      customerId,
      branchId,
      productType,
      orderType,
      dlabCustomer,
      customerCategory,
      primaryBranchId,
    };
  };

  fetchReport = () => {
    const filters = this.getFilters();
    const validationError = validateReportDateRange(
      filters.startDate,
      filters.endDate
    );
    if (validationError) {
      reportErrorToast({ message: validationError });
      return;
    }

    const params = buildReportParams(filters);
    this.setState({ loading: true });

    getOrderCountReport(params)
      .then((response) => {
        if (isReportSuccess(response)) {
          const data = response.data.data;
          this.setState({
            reportData: data,
            lnamGroupOptions:
              data && data.lnam_groups && data.lnam_groups.length
                ? data.lnam_groups
                : DEFAULT_LNAM_GROUPS,
            loading: false,
            filtersOpen: false,
          });
        } else {
          this.setState({ loading: false });
          reportErrorToast(null, response);
        }
      })
      .catch((error) => {
        this.setState({ loading: false });
        reportErrorToast(error);
        reportError(error);
      });
  };

  handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      this.setState({ dateRange: getDefaultDateRange() });
      return;
    }
    const validationError = validateReportDateRange(dates[0], dates[1]);
    if (validationError) {
      reportErrorToast({ message: validationError });
      return;
    }
    this.setState({ dateRange: dates });
  };

  disabledDate = (current) => {
    if (!current) return false;
    if (current.isAfter(moment(), "day")) return true;
    const { dateRange } = this.state;
    if (dateRange[0] && !dateRange[1]) {
      const min = dateRange[0].clone().subtract(MAX_REPORT_RANGE_DAYS - 1, "days");
      const max = dateRange[0].clone().add(MAX_REPORT_RANGE_DAYS - 1, "days");
      const cappedMax = max.isAfter(moment(), "day") ? moment() : max;
      return current.isBefore(min, "day") || current.isAfter(cappedMax, "day");
    }
    return false;
  };

  handleReset = () => {
    const { customerLocked, customerId } = this.state;
    this.setState(
      {
        dateRange: getDefaultDateRange(),
        status: "",
        customerId: customerLocked ? customerId : undefined,
        branchId: undefined,
        productType: undefined,
        orderType: "",
        dlabCustomer: "",
        customerCategory: "",
        primaryBranchId: undefined,
        filtersOpen: true,
      },
      this.fetchReport
    );
  };

  toggleFilters = () => {
    this.setState((prev) => ({ filtersOpen: !prev.filtersOpen }));
  };

  renderFilterFields = () => {
    const {
      dateRange,
      status,
      customerId,
      branchId,
      productType,
      orderType,
      dlabCustomer,
      customerCategory,
      primaryBranchId,
      customerList,
      branchList,
      lnamGroupOptions,
      customerLocked,
    } = this.state;

    return (
      <div className="report-filters-grid">
        <div className="report-filter-field report-filter-field--wide">
          <label htmlFor="report-date-range">Date range</label>
          <RangePicker
            id="report-date-range"
            value={dateRange}
            onChange={this.handleDateChange}
            disabledDate={this.disabledDate}
            format="MMM D, YYYY"
            allowClear={false}
            size="large"
            style={{ width: "100%" }}
          />
          <span className="report-filter-hint">
            Max {MAX_REPORT_RANGE_DAYS} days
          </span>
        </div>

        <div className="report-filter-field">
          <label>Order status</label>
          <Select
            value={status}
            onChange={(val) => this.setState({ status: val })}
            size="large"
            style={{ width: "100%" }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <Option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>Customer category</label>
          <Select
            value={customerCategory}
            onChange={(val) => this.setState({ customerCategory: val })}
            size="large"
            style={{ width: "100%" }}
          >
            {CUSTOMER_CATEGORY_OPTIONS.map((opt) => (
              <Option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>Customer</label>
          <Select
            showSearch={!customerLocked}
            allowClear={!customerLocked}
            placeholder={customerLocked ? "Your customer" : "All customers"}
            value={customerId}
            onChange={(val) => this.setState({ customerId: val })}
            disabled={customerLocked}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            size="large"
            style={{ width: "100%" }}
          >
            {customerList.map((item) => (
              <Option key={item.key} value={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>Manufacturing branch</label>
          <Select
            showSearch
            allowClear
            placeholder="All branches"
            value={branchId}
            onChange={(val) => this.setState({ branchId: val })}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            size="large"
            style={{ width: "100%" }}
          >
            {branchList.map((item) => (
              <Option key={item.key} value={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>Primary branch</label>
          <Select
            showSearch
            allowClear
            placeholder="All primary branches"
            value={primaryBranchId}
            onChange={(val) => this.setState({ primaryBranchId: val })}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            size="large"
            style={{ width: "100%" }}
          >
            {branchList.map((item) => (
              <Option key={`primary-${item.key}`} value={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>LNAM group</label>
          <Select
            allowClear
            placeholder="All LNAM groups"
            value={productType}
            onChange={(val) => this.setState({ productType: val })}
            size="large"
            style={{ width: "100%" }}
          >
            {lnamGroupOptions.map((group) => (
              <Option key={group} value={group}>
                {group}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>Order type</label>
          <Select
            value={orderType}
            onChange={(val) => this.setState({ orderType: val })}
            size="large"
            style={{ width: "100%" }}
          >
            {ORDER_TYPE_OPTIONS.map((opt) => (
              <Option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        <div className="report-filter-field">
          <label>DLAB customer</label>
          <Select
            value={dlabCustomer}
            onChange={(val) => this.setState({ dlabCustomer: val })}
            size="large"
            style={{ width: "100%" }}
          >
            {DLAB_OPTIONS.map((opt) => (
              <Option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
    );
  };

  renderSummaryCards = () => {
    const { reportData } = this.state;
    if (!reportData || !reportData.summary) return null;

    return (
      <div className="report-summary-grid">
        {SUMMARY_CARD_CONFIG.map((card) => {
          const metric = reportData.summary[card.key];
          if (!metric) return null;

          const value = getSummaryMetricValue(metric, card.valueKey);

          return (
            <div
              key={card.key}
              className={`report-summary-card report-summary-card--${card.key}`}
            >
              <p className="report-summary-card__label">{card.title}</p>
              <p className="report-summary-card__value">
                {value != null ? value : 0}
              </p>
              {card.trends.map((trend, index) => {
                const change = getSummaryMetricChange(metric, trend.changeKey);
                const changeLabel = formatChangeLabel(change);
                if (!changeLabel) return null;

                return (
                  <p
                    key={`${card.key}-${trend.changeKey}`}
                    className={`report-summary-card__change ${getSummaryChangeClass(
                      change
                    )}${
                      index > 0 ? " report-summary-card__change--secondary" : ""
                    }`}
                  >
                    {changeLabel} {trend.subtitle}
                  </p>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  getMatrixColumns = () => {
    const { reportData } = this.state;
    const lnamGroups =
      reportData && reportData.lnam_groups && reportData.lnam_groups.length
        ? reportData.lnam_groups
        : DEFAULT_LNAM_GROUPS;

    const columns = [
      {
        title: "Branch",
        dataIndex: "branch",
        key: "branch",
        width: 140,
      },
    ];

    lnamGroups.forEach((group) => {
      columns.push({
        title: group,
        dataIndex: ["groups", group],
        key: `group-${group}`,
        align: "center",
        width: 112,
        render: (val) => (val != null ? val : 0),
      });
    });

    columns.push({
      title: "Other",
      dataIndex: "other",
      key: "other",
      align: "center",
      width: 88,
      render: (val) => (val != null ? val : 0),
    });

    columns.push({
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "center",
      width: 108,
      className: "report-matrix-total-col",
      render: (val) => <strong>{val != null ? val : 0}</strong>,
    });

    return columns;
  };

  getMatrixScrollWidth = () => {
    const columns = this.getMatrixColumns();
    return columns.reduce((sum, col) => sum + (col.width || 100), 0);
  };

  countActiveFilters = () => {
    const {
      dateRange,
      status,
      customerId,
      branchId,
      productType,
      orderType,
      dlabCustomer,
      customerCategory,
      primaryBranchId,
    } = this.state;
    let count = 0;
    const defaultRange = getDefaultDateRange();
    const isDefaultDate =
      dateRange[0].isSame(defaultRange[0], "day") &&
      dateRange[1].isSame(defaultRange[1], "day");
    if (!isDefaultDate) count += 1;
    if (status) count += 1;
    if (customerId) count += 1;
    if (branchId) count += 1;
    if (productType) count += 1;
    if (orderType) count += 1;
    if (dlabCustomer !== "") count += 1;
    if (customerCategory) count += 1;
    if (primaryBranchId) count += 1;
    return count;
  };

  render() {
    const { loading, reportData, filtersOpen } = this.state;

    const matrixData =
      reportData && reportData.branch_product_matrix
        ? reportData.branch_product_matrix.map((row) => ({
            ...row,
            key: row.branch_id || row.branch,
          }))
        : [];

    const activeFilters = this.countActiveFilters();

    return (
      <div className="reports-shell">
        <Sidebar />
        <NavBar />
        <main className="reports-page reports-page--detail">
          <header className="report-detail-header">
            <div className="report-detail-header__top">
              <Link to="/reports" className="report-back-link">
                <Icon type="arrow-left" /> All reports
              </Link>
              <div className="report-header-actions">
                <button
                  type="button"
                  className={`report-filters-toggle${
                    filtersOpen ? " is-active" : ""
                  }`}
                  onClick={this.toggleFilters}
                >
                  <Icon type="filter" />
                  Filters
                  {activeFilters > 0 && (
                    <span className="report-filters-toggle__badge">
                      {activeFilters}
                    </span>
                  )}
                </button>
                <Button
                  type="primary"
                  size="large"
                  icon="search"
                  loading={loading}
                  className="report-run-btn"
                  onClick={this.fetchReport}
                >
                  Run report
                </Button>
              </div>
            </div>
            <h1 className="reports-page__title">Order Count Report</h1>
            {reportData && reportData.period && (
              <p className="report-period-label">
                <Icon type="calendar" />
                {moment(reportData.period.start_date).format("MMM D, YYYY")}
                {" — "}
                {moment(reportData.period.end_date).format("MMM D, YYYY")}
              </p>
            )}
          </header>

          <Modal
            title="Report filters"
            visible={filtersOpen}
            onCancel={this.toggleFilters}
            footer={[
              <Button key="reset" size="large" onClick={this.handleReset}>
                Reset
              </Button>,
              <Button
                key="run"
                type="primary"
                size="large"
                icon="search"
                loading={loading}
                onClick={this.fetchReport}
              >
                Run report
              </Button>,
            ]}
            width={760}
            className="report-filters-modal"
            maskClosable
            destroyOnClose={false}
          >
            {this.renderFilterFields()}
          </Modal>

          <Spin spinning={loading}>
            <section className="report-results">
              {!reportData && !loading && (
                <Empty description="Run the report to see results" />
              )}

              {reportData && (
                <React.Fragment>
                  {this.renderSummaryCards()}

                  <div className="report-table-card">
                    <div className="report-table-card__header">
                      <h2>Branch × LNAM Matrix</h2>
                      <p>
                        Order counts by manufacturing branch and LNAM group
                      </p>
                    </div>
                    <div className="report-table-scroll">
                      <Table
                        columns={this.getMatrixColumns()}
                        dataSource={matrixData}
                        pagination={false}
                        bordered
                        size="middle"
                        scroll={{ x: this.getMatrixScrollWidth() }}
                        locale={{ emptyText: "No data for selected filters" }}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
            </section>
          </Spin>
        </main>
      </div>
    );
  }
}

export default OrderCountReportPage;
